import { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { AuthLayoutContainer } from 'components/Layouts/AuthLayout';
import { Typography, Input, Button, Alert, colors, styles, Loading } from '@leapeasy/ui-kit';
import { Box } from '@mui/material';
import {
  acceptInvite,
  checkInviteToken,
  clearAcceptInviteResult,
  sendDeclineReason,
  userVerifyResponse,
} from 'store/actions/authActions';
import { failedNotif, successNotif } from 'store/actions/notifActions';
import { useNotificationMessages } from 'hooks/useNotificationMessages';
import { GlobalUXState } from 'components/GlobalUXState';

const isExpiredInviteMessage = (value = '') =>
  /(expired|invalid|token|already used|no longer valid)/i.test(String(value));

const roleMismatchMessage = (existingRole, inviteRole) =>
  `This email already has a Leap Portal account as ${existingRole || 'another role'}. Use a different email for this ${inviteRole || 'role'} invitation, or ask an administrator for help.`;

export const Invite = () => {
  const { token, type } = useParams();
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const userInfoState = useSelector((state) => state.getIn(['auth', 'userInfo']));
  const accountExists = userInfoState?.get('accountExists') === true;
  const roleMismatch = userInfoState?.get('roleMismatch') === true;
  const inviteEmail = userInfoState?.get('email') || '';
  const inviteRole = userInfoState?.get('role') || '';
  const existingRole = userInfoState?.get('existingRole') || '';

  const acceptResultState = useSelector((state) => state.getIn(['auth', 'acceptInviteResult']));
  const acceptStatus = acceptResultState?.get('status') || null;
  const acceptMessage = acceptResultState?.get('message') || '';
  const acceptEmail = acceptResultState?.get('email') || '';

  const { successText, failedText, hasSuccess, hasError } = useNotificationMessages();

  const [declineReason, setDeclineReason] = useState('');
  const [checking, setChecking] = useState(true);
  const [acceptingExisting, setAcceptingExisting] = useState(false);
  const [localAcceptError, setLocalAcceptError] = useState('');
  const acceptStartedRef = useRef(false);

  useEffect(() => {
    dispatch(userVerifyResponse(null));
    dispatch(clearAcceptInviteResult());
    dispatch(successNotif(null));
    dispatch(failedNotif(null));
    acceptStartedRef.current = false;
    setAcceptingExisting(false);
    setLocalAcceptError('');
    setChecking(true);
    dispatch(checkInviteToken(token));
  }, [dispatch, token]);

  useEffect(() => {
    if (userInfoState || hasError) {
      setChecking(false);
    }
  }, [userInfoState, hasError]);

  useEffect(() => {
    if (!userInfoState || type !== 'accept') {
      return;
    }

    if (roleMismatch) {
      setLocalAcceptError(roleMismatchMessage(existingRole, inviteRole));
      setAcceptingExisting(false);
      return;
    }

    if (accountExists) {
      if (acceptStartedRef.current) {
        return;
      }
      acceptStartedRef.current = true;
      setAcceptingExisting(true);
      setLocalAcceptError('');
      dispatch(acceptInvite(token));
      return;
    }

    navigate(`/register?token=${encodeURIComponent(token)}`, { replace: true });
  }, [
    userInfoState,
    accountExists,
    roleMismatch,
    existingRole,
    inviteRole,
    type,
    token,
    navigate,
    dispatch,
  ]);

  useEffect(() => {
    if (type !== 'accept' || !acceptingExisting || !acceptStatus) {
      return;
    }

    if (acceptStatus === 'success') {
      navigate('/login', {
        replace: true,
        state: {
          inviteAccepted: true,
          email: acceptEmail || inviteEmail || '',
          message:
            acceptMessage ||
            'Invitation accepted. Your property access is ready. Sign in to open the portal.',
        },
      });
      return;
    }

    if (acceptStatus === 'error') {
      setAcceptingExisting(false);
      setLocalAcceptError(acceptMessage || 'Unable to accept invitation.');
    }
  }, [type, acceptingExisting, acceptStatus, acceptMessage, acceptEmail, inviteEmail, navigate]);

  useEffect(() => {
    if (!userInfoState || type !== 'request') {
      return;
    }
    const returnUrl = encodeURIComponent('/notification?tab=received');
    navigate(`/login?returnUrl=${returnUrl}`, { replace: true });
  }, [userInfoState, type, navigate]);

  const onSubmitDecline = useCallback(() => {
    dispatch(sendDeclineReason({ decline: declineReason, token }));
  }, [declineReason, dispatch, token]);

  const isAccept = type === 'accept';
  const isDeclineSubmitted = hasSuccess && !acceptingExisting;
  const acceptErrorText = localAcceptError || (isAccept && hasError ? failedText : '');
  const showAcceptExpiredState =
    Boolean(acceptErrorText) && isExpiredInviteMessage(acceptErrorText);
  const showDeclineExpiredState = hasError && isExpiredInviteMessage(failedText);

  if (isAccept) {
    return (
      <AuthLayoutContainer showGrayLogo>
        <Box display="flex" flexDirection="column" gap="24px">
          <Typography variant="h2" style={{ color: colors.purple[900], fontWeight: 500 }}>
            Invitation
          </Typography>
          {(checking || acceptingExisting) && !acceptErrorText && (
            <Box display="flex" flexDirection="column" alignItems="center" gap="16px">
              <Loading size="large" />
              <Typography variant="body1" style={{ color: colors.neutral[700] }}>
                {acceptingExisting
                  ? 'Applying your property access…'
                  : 'Validating your invitation…'}
              </Typography>
            </Box>
          )}
          {showAcceptExpiredState ? (
            <GlobalUXState
              variant="expiredInvite"
              compact
              actionLabel="Request new invite"
              onAction={() => navigate('/lost-invite')}
            />
          ) : acceptErrorText ? (
            <>
              <Alert
                type="error"
                variant="contained"
                showBorder
                description={acceptErrorText}
                showClose={false}
              />
              <Button
                variant="tertiary"
                size="medium"
                onClick={() => navigate('/login')}
                style={{
                  width: 'fit-content',
                  paddingLeft: styles.spacing.space6,
                  paddingRight: styles.spacing.space6,
                }}
              >
                Back to login
              </Button>
            </>
          ) : null}
        </Box>
      </AuthLayoutContainer>
    );
  }

  return (
    <AuthLayoutContainer showGrayLogo>
      <Box display="flex" flexDirection="column" gap="24px">
        <Box>
          <Typography variant="h2" style={{ color: colors.purple[900], fontWeight: 500 }}>
            Decline invitation
          </Typography>
          <Typography variant="body1" style={{ color: colors.neutral[900] }}>
            {isDeclineSubmitted
              ? 'Your response has been recorded.'
              : 'Please share why you are declining this invitation.'}
          </Typography>
        </Box>

        <Box sx={{ border: '1px solid #F8F4F9', height: '0px' }} />

        {checking && !hasError && !isDeclineSubmitted && (
          <Box display="flex" flexDirection="column" alignItems="center" gap="16px">
            <Loading size="large" />
            <Typography variant="body1" style={{ color: colors.neutral[700] }}>
              Validating your invitation…
            </Typography>
          </Box>
        )}

        {showDeclineExpiredState ? (
          <GlobalUXState
            variant="expiredInvite"
            compact
            actionLabel="Request new invite"
            onAction={() => navigate('/lost-invite')}
          />
        ) : hasError ? (
          <Alert
            type="error"
            variant="contained"
            showBorder
            description={failedText}
            showClose={false}
          />
        ) : null}

        {hasSuccess && (
          <Alert
            type="success"
            variant="contained"
            showBorder
            description={successText}
            showClose={false}
          />
        )}

        {!hasError && !isDeclineSubmitted && !checking && (
          <>
            <Input
              value={declineReason}
              onChange={(e) => setDeclineReason(e.target.value)}
              label="Decline reason"
              minWidth="100%"
              fullWidth
              size="medium"
              showCopy={false}
              showHelperOnTyping={false}
            />

            <Button
              variant="primary"
              size="medium"
              onClick={onSubmitDecline}
              disabled={!declineReason?.trim()}
              style={{ minWidth: '100%' }}
            >
              Submit
            </Button>
          </>
        )}

        {(isDeclineSubmitted || hasError) && (
          <Button
            variant="tertiary"
            size="medium"
            onClick={() => navigate('/login')}
            style={{
              width: 'fit-content',
              paddingLeft: styles.spacing.space6,
              paddingRight: styles.spacing.space6,
            }}
          >
            Back to login
          </Button>
        )}
      </Box>
    </AuthLayoutContainer>
  );
};
